import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import PageTransition from "@/components/PageTransition";
import { getLeaderboard, getPositions, getWallet, calcPending, fmtNum, shortAddr } from "@/lib/wallet";
import { Crown, Medal } from "lucide-react";

export default function Leaderboard() {
  const [items, setItems] = useState([]);
  const [me, setMe] = useState(null);

  useEffect(() => {
    setItems(getLeaderboard());
    const w = getWallet();
    if (w) {
      const positions = getPositions();
      const totalStaked = positions.reduce((a, p) => a + p.amount, 0);
      const earned = positions.reduce((a, p) => a + calcPending(p), 0);
      setMe({ handle: shortAddr(w.address), totalStaked, earned, chain: w.chain });
    }
  }, []);

  const merged = useMemo(() => {
    if (!me || me.totalStaked === 0) return items;
    // Insert "you" into the list ranked by totalStaked
    const arr = [...items, { ...me, isMe: true }].sort((a, b) => b.totalStaked - a.totalStaked);
    return arr.map((r, i) => ({ ...r, rank: i + 1 })).slice(0, 16);
  }, [items, me]);

  return (
    <PageTransition>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-20">
        <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "var(--text-3)" }}>▍ Top stakers</div>
        <h1 className="font-display font-black text-5xl tracking-tighter">Leaderboard</h1>
        <p className="mt-3 text-sm max-w-xl" style={{ color: "var(--text-2)" }}>
          Cumulative USD-equivalent of all stakers across both networks. Refreshed live.
        </p>

        <div className="border mt-10" style={{ borderColor: "var(--border)", background: "var(--surface-1)" }}>
          <div className="grid grid-cols-12 px-6 py-3 text-[10px] uppercase tracking-widest border-b" style={{ borderColor: "var(--border)", color: "var(--text-3)" }}>
            <div className="col-span-1">#</div>
            <div className="col-span-5">Staker</div>
            <div className="col-span-2">Chain</div>
            <div className="col-span-2 text-right">Total Staked</div>
            <div className="col-span-2 text-right">Earned</div>
          </div>
          {merged.map((r) => (
            <div
              key={r.rank + r.handle}
              data-testid={`leaderboard-row-${r.rank}`}
              className="grid grid-cols-12 px-6 py-4 border-b items-center text-sm"
              style={{
                borderColor: "var(--border)",
                background: r.isMe ? "rgba(255,255,255,0.04)" : "transparent",
              }}
            >
              <div className="col-span-1 font-mono">
                {r.rank === 1 ? <Crown size={14} className="inline" style={{ color: "var(--warning)" }} /> :
                 r.rank === 2 ? <Medal size={14} className="inline" style={{ color: "#C0C0C0" }} /> :
                 r.rank === 3 ? <Medal size={14} className="inline" style={{ color: "#CD7F32" }} /> :
                 r.rank}
              </div>
              <div className="col-span-5 font-mono flex items-center gap-2">
                {r.handle}
                {r.isMe && <span className="px-1.5 py-0.5 text-[9px] tracking-widest" style={{ background: "#fff", color: "#000" }}>YOU</span>}
              </div>
              <div className="col-span-2 text-[10px] uppercase tracking-widest" style={{ color: r.chain === "evm" ? "var(--evm)" : "var(--sol)" }}>
                {r.chain}
              </div>
              <div className="col-span-2 text-right font-mono">${fmtNum(r.totalStaked, 0)}</div>
              <div className="col-span-2 text-right font-mono" style={{ color: "var(--success)" }}>+${fmtNum(r.earned, 0)}</div>
            </div>
          ))}
        </div>
      </section>
    </PageTransition>
  );
}
